import type { IncidentReportSectionType } from "components/CriticalIncident/interfaces";
import { COUNTRY_CODE, useProviderCountryCode } from "utils/regionUtils";

export type ReportNavigationKey = IncidentReportSectionType | 'review';

export interface ReportMenuItem {
  labelKey: ReportNavigationKey;
  url: string;
  active: boolean;
}

export const DEFAULT_REPORT_SECTIONS: ReportMenuItem[] = [
  { labelKey: 'interventionSummary', url: 'intervention-summary', active: true },
  { labelKey: 'incidentDocumentation', url: 'incident-documentation', active: true },
  { labelKey: 'individualSupportSession', url: 'individual-support-session', active: true },
  { labelKey: 'groupService', url: 'group-service', active: true },
  { labelKey: 'onsiteContact', url: 'onsite-contact', active: true },
  { labelKey: 'followUp', url: 'follow-ups', active: true },
  { labelKey: 'recommendation', url: 'recommendations', active: true },
  { labelKey: 'riskAssessment', url: 'risk-assessments', active: true },
  { labelKey: 'review', url: 'review', active: false },
];

// AU order per TPH-733's AC; riskAssessment/followUp are never shown — their data lives
// inside individualSupportSession instead (embedded Risk Assessment / Duty-to-Report /
// Follow-up Sessions blocks per TPH-1906/1907/1908/1909), and the two new sections
// (TPH-1890/1903) have no non-AU equivalent
export const AU_REPORT_SECTIONS: ReportMenuItem[] = [
  { labelKey: 'interventionSummary', url: 'intervention-summary', active: true },
  { labelKey: 'incidentDocumentation', url: 'incident-documentation', active: true },
  { labelKey: 'deliveryHours', url: 'delivery-hours', active: true },
  { labelKey: 'onsiteContact', url: 'onsite-contact', active: true },
  { labelKey: 'referringManager', url: 'referring-manager', active: true },
  { labelKey: 'groupService', url: 'group-service', active: true },
  { labelKey: 'individualSupportSession', url: 'individual-support-session', active: true },
  { labelKey: 'recommendation', url: 'recommendations', active: true },
  { labelKey: 'review', url: 'review', active: false },
];

export const DEFAULT_ALL_SECTIONS: IncidentReportSectionType[] = ['followUp', 'groupService', 'incidentDocumentation', 'individualSupportSession', 'interventionSummary', 'onsiteContact', 'recommendation', 'riskAssessment'];

// drives the "unlock Review" check for AU — deliberately excludes the two new stub sections
// (no backend contract yet) and riskAssessment/followUp (AU never shows them)
export const AU_ALL_SECTIONS: IncidentReportSectionType[] = ['groupService', 'incidentDocumentation', 'individualSupportSession', 'interventionSummary', 'onsiteContact', 'recommendation'];

// superset used only for default-value backfilling — safe regardless of region
export const ALL_SECTION_KEYS: IncidentReportSectionType[] = [...DEFAULT_ALL_SECTIONS, 'referringManager', 'deliveryHours'];

export interface ReportSectionsConfig {
  isAU: boolean;
  reportSections: ReportMenuItem[];
  allSections: IncidentReportSectionType[];
}

// API versioning (v1/v2) is resolved internally by the incidentReportQuery hooks
// themselves (see useController in api/queries/incidentReportQuery.ts) — this config
// is nav/section shape only and doesn't need to expose an apiVersion for callers.
export function useReportSectionsConfig(providerId?: number): ReportSectionsConfig {
  const isAU = useProviderCountryCode(providerId) === COUNTRY_CODE.AUSTRALIA;

  return isAU
    ? { isAU, reportSections: AU_REPORT_SECTIONS, allSections: AU_ALL_SECTIONS }
    : { isAU, reportSections: DEFAULT_REPORT_SECTIONS, allSections: DEFAULT_ALL_SECTIONS };
}
